import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Video, CheckCircle, ArrowRight, Users, Camera, FileText, Image } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const QUALIFIER_URL = 'https://mikelmedia-qualify.paperform.co';
const BOOKING_URL = 'https://bookme.name/professionalchameleon/consultation';

const ContentCreationPage = () => {
  const { t, i18n } = useTranslation();
  const isES = i18n.language?.startsWith('es');
  const color = '#4EBFBF';

  const includes_en = [
    'Content strategy & monthly editorial calendar',
    'Professional video production & editing (Reels, TikTok, YouTube)',
    'Photography (product, team, lifestyle)',
    'Copywriting (website, ads, email, social captions)',
    'Blog posts & long-form SEO content',
    'Content repurposing across all formats & platforms',
  ];

  const includes_es = [
    'Estrategia de contenido y calendario editorial mensual',
    'Producción y edición de video profesional (Reels, TikTok, YouTube)',
    'Fotografía (producto, equipo, lifestyle)',
    'Redacción (sitio web, anuncios, email, captions)',
    'Blog posts y contenido SEO de formato largo',
    'Reutilización de contenido en todos los formatos y plataformas',
  ];

  const for_en = [
    'Brands that need a steady stream of quality content every month',
    'Businesses launching a new website, campaign, or product',
    'Companies that want to stand out with premium visuals and copy',
    'Anyone who struggles to produce content consistently on their own',
  ];

  const for_es = [
    'Marcas que necesitan contenido de calidad constante cada mes',
    'Negocios lanzando un nuevo sitio web, campaña o producto',
    'Empresas que quieren destacarse con visuales y textos premium',
    'Quienes tienen dificultad para producir contenido de forma consistente',
  ];

  const contentTypes = [
    { icon: Video, en: 'Video & Reels', es: 'Video & Reels' },
    { icon: Camera, en: 'Photography', es: 'Fotografía' },
    { icon: FileText, en: 'Copywriting', es: 'Redacción' },
    { icon: Image, en: 'Graphic Design', es: 'Diseño Gráfico' },
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-[#0F1C2E] flex flex-col">
      <Navbar />

      <main className="flex-1">

        {/* HERO */}
        <section
          className="pt-24 pb-20 px-4"
          style={{ background: `linear-gradient(135deg, ${color}18 0%, ${color}05 100%)` }}
        >
          <div className="max-w-5xl mx-auto">
            <Link
              to="/#servicios"
              className="inline-flex items-center gap-1 text-sm font-semibold mb-8 transition-opacity hover:opacity-70 font-source-sans"
              style={{ color }}
            >
              {t('service_page.back')}
            </Link>

            <div className="flex flex-col md:flex-row md:items-start gap-8">
              <div
                className="flex-shrink-0 w-20 h-20 flex items-center justify-center"
                style={{ backgroundColor: color + '20' }}
              >
                <Video size={36} style={{ color }} />
              </div>

              <div>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-2.5 h-2.5" style={{ transform: 'rotate(45deg)', backgroundColor: color }} />
                  <span className="text-xs font-bold tracking-widest uppercase font-poppins" style={{ color }}>
                    {isES ? 'Creación de Contenido' : 'Content Creation'}
                  </span>
                </div>

                <h1 className="font-poppins font-bold text-3xl sm:text-4xl lg:text-5xl text-[#233559] dark:text-white leading-tight mb-5">
                  {isES
                    ? 'Contenido que Para el Scroll y Vende'
                    : 'Content That Stops the Scroll and Sells'}
                </h1>

                <p className="text-lg text-[#233559]/65 dark:text-white/60 leading-relaxed max-w-2xl font-source-sans">
                  {isES
                    ? 'Producimos contenido visual y escrito de alta calidad — desde videos y fotos hasta copy y blogs — que representa auténticamente tu marca y convierte a tu audiencia en clientes.'
                    : 'We produce high-quality visual and written content — from video and photography to copywriting and blogs — that authentically represents your brand and turns your audience into clients.'}
                </p>

                <p className="mt-4 text-sm font-semibold font-source-sans" style={{ color }}>
                  {isES ? 'Desde $500 / mes' : 'Starting at $500 / month'}
                </p>

                <div className="flex flex-wrap items-center gap-4 mt-8">
                  <a
                    href={QUALIFIER_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-7 py-3.5 font-poppins font-semibold text-white text-sm tracking-wide transition-opacity hover:opacity-90"
                    style={{ backgroundColor: color }}
                  >
                    {isES ? 'Quiero contenido que convierta' : 'Get content that converts'}
                    <ArrowRight size={16} />
                  </a>
                  <a
                    href={BOOKING_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-7 py-3.5 font-poppins font-semibold text-sm tracking-wide border-2 transition-opacity hover:opacity-80"
                    style={{ color, borderColor: color, backgroundColor: 'transparent' }}
                  >
                    {isES ? 'Reservar consulta' : 'Book a consultation'}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CONTENT TYPES PILLS */}
        <section className="py-12 px-4 bg-[#233559] dark:bg-[#1A2639]">
          <div className="max-w-3xl mx-auto flex flex-wrap items-center justify-center gap-6">
            {contentTypes.map(({ icon: Icon, en, es }, i) => (
              <div key={i} className="flex items-center gap-3 text-white/80">
                <div
                  className="w-10 h-10 flex items-center justify-center"
                  style={{ backgroundColor: color + '25' }}
                >
                  <Icon size={18} style={{ color }} />
                </div>
                <span className="font-poppins font-semibold text-sm">{isES ? es : en}</span>
              </div>
            ))}
          </div>
        </section>

        {/* WHAT'S INCLUDED + WHO IT'S FOR */}
        <section className="py-20 px-4 bg-white dark:bg-[#0F1C2E]">
          <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12">

            <div>
              <div className="flex items-center gap-3 mb-8">
                <div className="w-3 h-3" style={{ transform: 'rotate(45deg)', backgroundColor: color }} />
                <h2 className="font-poppins font-bold text-2xl text-[#233559] dark:text-white">
                  {t('service_page.includes_title')}
                </h2>
              </div>
              <ul className="space-y-4">
                {includes_en.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <CheckCircle size={20} className="flex-shrink-0 mt-0.5" style={{ color }} />
                    <span className="text-[#233559]/75 dark:text-white/65 font-source-sans leading-relaxed">
                      {isES ? includes_es[idx] : item}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <div className="flex items-center gap-3 mb-8">
                <div className="w-3 h-3" style={{ transform: 'rotate(45deg)', backgroundColor: color }} />
                <h2 className="font-poppins font-bold text-2xl text-[#233559] dark:text-white">
                  {t('service_page.for_title')}
                </h2>
              </div>
              <ul className="space-y-4">
                {for_en.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <Users size={20} className="flex-shrink-0 mt-0.5" style={{ color }} />
                    <span className="text-[#233559]/75 dark:text-white/65 font-source-sans leading-relaxed">
                      {isES ? for_es[idx] : item}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* SAMPLE DELIVERABLES */}
        <section className="py-20 px-4" style={{ backgroundColor: color + '08' }}>
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-14">
              <div className="flex items-center justify-center gap-2 mb-3">
                <div className="w-3 h-3" style={{ transform: 'rotate(45deg)', backgroundColor: color }} />
                <span className="text-xs font-bold tracking-widest uppercase font-poppins" style={{ color }}>
                  {isES ? 'Ejemplo Mensual' : 'Monthly Example'}
                </span>
              </div>
              <h2 className="font-poppins font-bold text-3xl text-[#233559] dark:text-white">
                {isES ? 'Lo que Recibes en un Mes' : 'What You Get in One Month'}
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { num: '8', en: 'Social media posts (graphics + copy)', es: 'Posts para redes sociales (gráficos + copy)' },
                { num: '4', en: 'Short-form videos (Reels / TikTok)', es: 'Videos de formato corto (Reels / TikTok)' },
                { num: '2', en: 'Blog articles (SEO-optimized)', es: 'Artículos de blog (optimizados para SEO)' },
                { num: '1', en: 'Email newsletter', es: 'Newsletter por email' },
                { num: '2', en: 'Ad creatives (static or video)', es: 'Creativos para anuncios (estáticos o video)' },
                { num: '∞', en: 'Revisions until you love it', es: 'Revisiones hasta que lo ames' },
              ].map((d, i) => (
                <div key={i} className="bg-white dark:bg-[#1A2639] p-6 border border-[#233559]/08 dark:border-white/08">
                  <div className="font-poppins font-bold text-4xl mb-3" style={{ color }}>{d.num}</div>
                  <p className="text-[#233559]/70 dark:text-white/60 font-source-sans text-sm leading-relaxed">
                    {isES ? d.es : d.en}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* BOTTOM CTA */}
        <section className="py-20 px-4" style={{ backgroundColor: color }}>
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-poppins font-bold text-3xl sm:text-4xl text-white mb-4">
              {isES
                ? '¿Listo para tener contenido que trabaje por ti?'
                : 'Ready for content that works for you?'}
            </h2>
            <p className="text-white/80 text-lg mb-8 font-source-sans">
              {isES
                ? 'Reserva tu consulta directamente o cuéntanos sobre tu marca.'
                : 'Book your consultation directly or tell us about your brand.'}
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href={BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-8 py-4 bg-white font-poppins font-bold text-sm tracking-wide transition-opacity hover:opacity-90"
                style={{ color }}
              >
                {isES ? 'Reservar consulta' : 'Book a consultation'}
                <ArrowRight size={16} />
              </a>
              <a
                href={QUALIFIER_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-8 py-4 font-poppins font-semibold text-sm tracking-wide border-2 border-white/60 text-white transition-opacity hover:opacity-80"
              >
                {isES ? 'Aplicar primero' : 'Apply first'}
              </a>
            </div>
          </div>
        </section>

      </main>
      <Footer />
    </div>
  );
};

export default ContentCreationPage;
